if (!customElements.get('fake-variant-picker')) {
  /**
   * FakeVariantPicker - Custom element for selecting variants across multiple "fake" products
   * Handles navigation between related products while preserving variant selections.
   */
  class FakeVariantPicker extends HTMLElement {
    constructor() {
      super();

      this.selectedValueElements = this.querySelectorAll('.selected-value');
      this.addEventListener('click', this._onVariantClick.bind(this));
      this.addEventListener('change', this._onVariantChange.bind(this));

      // Parse product data from data attributes
      try {
        this.secondaryVariantProducts = JSON.parse(this.dataset.secondaryVariantProducts || '[]');
        this.primaryVariantProducts = JSON.parse(this.dataset.primaryVariantProducts || '[]');
        this.fakeVariants = this.primaryVariantProducts.concat(this.secondaryVariantProducts);
      } catch (e) {
        console.warn('[FakeVariantPicker] Error parsing products data');
        this.primaryVariantProducts = [];
        this.secondaryVariantProducts = [];
        this.fakeVariants = [];
      }

      // Check if we have valid products - if not, component will be disabled
      this.hasValidProducts = Array.isArray(this.fakeVariants) && this.fakeVariants.length > 0;

      // Parse current product data
      try {
        this.product = JSON.parse(this.dataset.product || '{}');
        if (!this.product || !this.product.id) {
          this.product = { id: null, title: '', handle: '', variants: [] };
        }
      } catch (e) {
        console.warn('[FakeVariantPicker] Error parsing product data');
        this.product = { id: null, title: '', handle: '', variants: [] };
      }

      this.productTitle = this.product.title || '';

      // Parse variant options for matching
      try {
        this.primaryVariantOptions = JSON.parse(this.dataset.primaryVariantOptions || '[]');
        if (!Array.isArray(this.primaryVariantOptions)) this.primaryVariantOptions = [];

        this.secondaryVariantOptions = JSON.parse(this.dataset.secondaryVariantOptions || '[]');
        if (!Array.isArray(this.secondaryVariantOptions)) this.secondaryVariantOptions = [];
      } catch (e) {
        console.warn('[FakeVariantPicker] Error parsing variant options');
        this.primaryVariantOptions = [];
        this.secondaryVariantOptions = [];
      }
    }

    // Component lifecycle method
    connectedCallback() {
      requestAnimationFrame(() => {
        this.highlightActiveVariantOption();
      });
    }

    /**
     * Select the default variant when the component loads
     * Prioritizes matching by product ID, then falls back to first input
     */
    highlightActiveVariantOption() {
      // If component is disabled due to missing products, don't proceed
      if (!this.hasValidProducts) return;
      if (!this.product || !this.product.id) return;

      // Handle primary and secondary groups separately
      this._highlightGroupVariants('primary');
      this._highlightGroupVariants('secondary');
    }

    /**
     * Highlight variants for a specific group (primary/secondary)
     * @param {string} group - 'primary' or 'secondary'
     */
    _highlightGroupVariants(group) {
      const currentProductId = this.product.id.toString();
      const isSecondary = group === 'secondary';
      const fieldsetIndex = isSecondary ? 1 : 0;
      const fieldset = this.querySelectorAll('fieldset')[fieldsetIndex];

      if (!fieldset) return;

      const selectedValueElement = fieldset.querySelector('.selected-value');
      const variantProducts = isSecondary ? this.secondaryVariantProducts : this.primaryVariantProducts;
      const offset = isSecondary ? this.primaryVariantProducts.length : 0;

      // Find the product in this group that matches the current product ID
      let matchingProductIndex = variantProducts.findIndex(
        product => product && product.id && product.id.toString() === currentProductId
      );

      const inputs = [...fieldset.querySelectorAll('input[name^="fake-variant-selector-"]')];
      let highlightInput = null;

      if (matchingProductIndex !== -1) {
        // If we found a match by product ID, use that
        const absoluteIndex = matchingProductIndex + offset;
        highlightInput = inputs.find(input =>
          parseInt(input.dataset.productIndex, 10) === absoluteIndex
        );
      }

      // If no match by product ID or no input found, use the first input
      if (!highlightInput && inputs.length > 0) {
        highlightInput = inputs[0];
      }

      // If we found an input to highlight
      if (highlightInput) {
        // Mark as selected and checked
        highlightInput.checked = true;
        const swatch = highlightInput.nextElementSibling;
        if (swatch) {
          swatch.classList.add('is-selected');
        }

        // Update the selected value text
        if (selectedValueElement) {
          selectedValueElement.textContent = highlightInput.value;
        }
      }
    }

    /**
     * Handle navigation to target product, preserving variant selection
     * @param {HTMLElement} target - The clicked/changed input element
     */
    _navigateToTargetProduct(target) {
      // Skip if component is disabled or target is invalid
      if (!this.hasValidProducts) return;
      if (!target || !this.product || !this.product.id) return;

      const productId = this.product.id;

      if (target.matches(`input[name="fake-variant-selector-${productId}"]`)) {
        const productIndex = parseInt(target.dataset.productIndex, 10);
        const targetProduct = this.fakeVariants[productIndex];

        if (targetProduct) {
          // Get current variant from URL
          const urlParams = new URLSearchParams(window.location.search);
          const currentVariantId = urlParams.get('variant');

          // Ensure product has variants array
          if (!Array.isArray(this.product.variants)) {
            this.product.variants = [];
          }

          // Find current variant
          const currentVariant = this.product.variants.find(v => v && v.id && v.id.toString() === currentVariantId) ||
            this.product.variants[0] || { id: null, title: '' };

          // Ensure target product has variants array
          if (!Array.isArray(targetProduct.variants)) {
            targetProduct.variants = [];
          }

          // Try to find a matching variant in the target product
          let matchingVariant = null;

          // For products with real variants (like sizes), we want to match the equivalent variant
          // First, try to identify what real option the user has selected (like size)
          const realOptionValue = this._getRealVariantOption(currentVariant);

          // Try to find a variant in the target product with the same real option
          if (realOptionValue) {
            matchingVariant = this._findVariantWithSameOption(targetProduct.variants, realOptionValue);
          }

          // If still no match, use first variant
          if (!matchingVariant) {
            matchingVariant = targetProduct.variants[0] || { id: null };
          }

          // Construct and navigate to URL
          const productUrl = `/products/${targetProduct.handle}`;
          const url = new URL(productUrl, window.location.origin);

          if (matchingVariant && matchingVariant.id) {
            url.searchParams.set('variant', matchingVariant.id);
          }

          window.location.href = url.toString();
        }
      }
    }

    /**
     * Event handler for variant click
     */
    _onVariantClick(event) {
      try {
        const target = event.target;
        this._navigateToTargetProduct(target);
      } catch (error) {
        console.error('[FakeVariantPicker] Error in variant click handler:', error);
      }
    }

    /**
     * Event handler for variant change
     */
    _onVariantChange(event) {
      try {
        const target = event.target;
        this._navigateToTargetProduct(target);
      } catch (error) {
        console.error('[FakeVariantPicker] Error in variant change handler:', error);
      }
    }

    /**
     * Extract the non-color option from a variant (usually size, letter, etc.)
     * @param {Object} variant - The variant to extract from
     * @returns {string|null} The real option value or null if not found
     */
    _getRealVariantOption(variant) {
      // Assumed to have only option1 which is the real variant to match.
      if (!variant || !variant.options || !Array.isArray(variant.options) || variant.options.length === 0) {
        return null;
      }

      // If we only have one option, check if it's not a color
      if (variant.option1) {
        return variant.option1;
      }

      return null;
    }

    /**
     * Find a variant with the same option value
     * @param {Array} variants - Array of variants to search
     * @param {string} optionValue - Option value to match
     * @returns {Object|null} Matching variant or null
     */
    _findVariantWithSameOption(variants, optionValue) {
      if (!variants || !Array.isArray(variants) || variants.length === 0 || !optionValue) {
        return null;
      }

      // Try to find a variant with matching option value
      for (const variant of variants) {
        if (variant.option1 === optionValue) {
          return variant;
        }
      }

      return null;
    }
  }

  customElements.define('fake-variant-picker', FakeVariantPicker);
}
